<template>
  <div class="relative">
    <div class="flex flex-wrap gap-2 pb-4 text-sm">
      <div class="w-full text-base">{{ t('trial_calculation_result_screening') }}</div>
      <div
        v-for="(num, numIdx) in info.data"
        :key="`num-${numIdx}`"
        class="w-32 bg-cdp-main-primary-light p-4"
      >
        <h3 class="mb-1 text-center text-lg text-cdp-main-primary-dark">
          {{ commonThousandths(num) }}
        </h3>
        <h3 class="text-center text-sm">
          {{ info.nameMap[numIdx] }}
        </h3>
      </div>
    </div>
    <div v-show="!props.hideToolbar" class="my-2 flex justify-end gap-2">
      <text-field v-model="table.search" :placeholder="t('plz_typing_mail_or_name')" />
      <button
        class="btn flex items-center border border-cdp-main-primary bg-cdp-main-primary-light text-cdp-main-primary hover:bg-cdp-primary-200"
        @click="
          getTable({
            reset: true,
            loading: true
          })
        "
      >
        {{ t('search') }}
        <icon name="ic_common_search" class="ml-1 h-5 w-5" />
      </button>
      <button
        class="disabled-btn btn bg-cdp-main-primary text-cdp-white"
        @click="downloadList"
      >
        {{ t('download_list') }}
      </button>
    </div>
    <div class="max-h-[70vh] overflow-auto">
      <data-table
        v-model:page="table.currentPage"
        v-model:pageSize="table.limit"
        :columns="table.columns"
        :rows="table.rows"
        :total="table.count"
        @change="
          getTable({
            reset: false,
            loading: true
          })
        "
      >
        <template #uniqId="{ item }">
          <router-link
            target="_blank"
            :to="`/data-overview/members/member-detail/${item.uniqId}`"
            class="text-cdp-main-primary hover:text-cdp-main-primary-dark"
          >
            {{ item.uniqId }}
          </router-link>
        </template>
      </data-table>
    </div>
    <div
      v-show="props.loading"
      class="absolute left-0 top-0 h-full w-full bg-cdp-main-primary-light/80"
    >
      <div
        class="flex h-full w-full items-center justify-center text-cdp-main-primary-dark"
      >
        {{ t('counting') }}
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';

import { ApiFiltrationsInfo, ApiFiltrationsResult } from '@/api/filtrations.js';

import { exportExcel } from '@/plugins/xlxs.js';

import { commonThousandths } from '@/composables/common.js';

import dataTable from '@/components/DataTable.vue';

import { reactive, watch, onMounted, onUnmounted, defineComponent } from 'vue';

export default defineComponent({
  name: 'ComponentsTableFilter',
  components: {
    dataTable
  },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    filterId: {
      type: String,
      default: ''
    },
    hideToolbar: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:loading'],
  setup(props, { emit }) {
    const { t } = useI18n();

    let runSchedule = null;

    const info = reactive({
      data: {
        totalCount: 0,
        unsubscribeCount: 0,
        invalidCount: 0,
        openRate: 0,
        clickRate: 0
      },
      nameMap: {
        totalCount: t('filter_count'),
        unsubscribeCount: t('do_not_disturb_number'),
        invalidCount: t('invalid_number'),
        openRate: t('open_rate'),
        clickRate: t('click_rate')
      }
    });

    const table = reactive({
      columns: [],
      rows: [],
      search: '',
      count: 0,
      limit: 10,
      currentPage: 1
    });

    watch(
      () => props.loading,
      (bool) => {
        if (bool) {
          runSchedule = setInterval(() => {
            getTable({
              reset: true,
              loading: false
            });
          }, 10000);
        }
      },
      { deep: true, immediate: true }
    );

    onMounted(() => {
      getTable({
        reset: true,
        loading: false
      });
    });

    onUnmounted(() => {
      // destroyed when leaving the page
      clearSchedule();
    });

    async function getInfo() {
      const form = {
        uuid: props.filterId
      };
      const data = await ApiFiltrationsInfo(form);
      if (!data.msg) {
        info.data = data;
      }
    }

    async function getTable({ reset, loading }) {
      if (reset) {
        table.currentPage = 1;
      }

      const form = {
        loading,
        uuid: props.filterId,
        search: table.search,
        limit: table.limit,
        offset: (table.currentPage - 1) * table.limit
      };

      const data = await ApiFiltrationsResult(form);
      if (data.results === false) return false;

      if (!data.msg && data.results) {
        if (data.results?.length) {
          table.columns = Object.keys(data.results[0])?.map((item) => ({
            label: item.toUpperCase(),
            field: item,
            sortable: false,
            width: '1%'
          }));
        }

        table.rows = data.results;
        table.count = data.count;

        await getInfo();
        clearSchedule();
        emit('update:loading', false);
      }
    }

    function clearSchedule() {
      if (runSchedule) clearInterval(runSchedule);
    }

    async function downloadList() {
      const form = {
        uuid: props.filterId
      };

      const data = await ApiFiltrationsResult(form);
      if (!data.msg) {
        const header = {};
        const key = [];
        if (data.results?.length) {
          Object.keys(data.results[0])?.forEach((itemKey) => {
            header[itemKey] = itemKey.toUpperCase();
            key.push(itemKey);
          });
        }

        const obj = {
          header,
          data: data.results,
          key,
          filename: t('filter_list'),
          autoWidth: true
        };
        exportExcel(obj);
      }
    }

    return {
      props,
      t,
      commonThousandths,
      info,
      table,
      getInfo,
      getTable,
      clearSchedule,
      downloadList
    };
  }
});
</script>